import { gql } from '@apollo/client'

/** What the wizard shows about how a new campaign can start: the recommendation and why. */
export const WARMUP_RECOMMENDATION = gql`
    query WarmupRecommendation(
        $publisherId: ID!
        $campaignId: ID!
        $objective: String!
        $targetEvent: String
        $audienceAdSets: Int
        $refresh: Boolean
    ) {
        warmupRecommendation(
            publisher_id: $publisherId
            campaign_id: $campaignId
            objective: $objective
            target_event: $targetEvent
            audience_ad_sets: $audienceAdSets
            refresh: $refresh
        ) {
            available
            unavailable_reason
            start_mode
            reason
            message
            target_event
            warmup_event
            checked_at
            events_status
            pixel_shared_with_ad_account
            events_truncated
            target_event_count
            seed_floor
            events_window_days
            audience_ad_sets
            events_needed
            below_events_needed
            warmup_candidates {
                event
                count
            }
            lookalikes_enabled
            automated_allowed
            holdout_enabled
            holdout_share
        }
    }
`

export const WARMUP_NOT_USED_NOTICE_FIELDS = gql`
    fragment WarmupNotUsedNoticeFields on WarmupNotUsedNotice {
        visible
        dismissible
        dismissed
        reason
        since
        ends_at
    }
`

export const WARMUP_FIELDS = gql`
    ${WARMUP_NOT_USED_NOTICE_FIELDS}
    fragment WarmupFields on AdCampaignWarmup {
        id
        ad_campaign_id
        state
        version
        mode
        start_choice
        start_mode
        target_event
        warmup_event
        warming_started_at
        proposal
        proposed_at
        declined_at
        expanded_at
        sweep_until
        cancel_choice
        campaign_status
        campaign_paused
        feature_enabled
        halted
        automated_allowed
        not_used_notice {
            ...WarmupNotUsedNoticeFields
        }
        holdout {
            arm
            share
            assigned_at
        }
        warmup_ad_set {
            id
            name
            external_id
            budget
            status
        }
        progress {
            count
            count_checked_at
            threshold
            window
            last_checked_at
            reason
            action
        }
        expansion {
            step
            attempts
            max_attempts
            lease_live
            started_at
            failure_reason
            failure_class
        }
        held_rows {
            id
            name
            budget
            status
            external_id
            held
            step
            skip_reason
            release_reason
            pending_reason
            event
            can_edit
            can_edit_budget
        }
        attention {
            kind
            ad_set_external_id
            name
            live_status
            detected_at
            resolved_at
            attempts
            text
        }
        can {
            confirm
            decline
            retry
            cancel
            set_mode
            remove_held_rows
            recheck
        }
    }
`

/**
 * The warm-up, the holdout assignment and the "warm-up not used" notice of up to 100 ad campaigns in one request, one
 * entry per id in the order asked. An id that cannot be read comes back with `found: false`.
 */
export const AD_CAMPAIGN_WARMUPS = gql`
    ${WARMUP_FIELDS}
    query AdCampaignWarmups($adCampaignIds: [ID!]!) {
        AdCampaign {
            adCampaignWarmups(adCampaignIds: $adCampaignIds) {
                ad_campaign_id
                found
                is_owner
                warmup {
                    ...WarmupFields
                }
                holdout {
                    arm
                    share
                    assigned_at
                }
                not_used_notice {
                    ...WarmupNotUsedNoticeFields
                }
                has_maximum
                max_daily_budget
                unconfirmed_changes {
                    ad_set_id
                    name
                    external_id
                    reason
                    since
                    held
                    role
                    optimiser
                }
                can_recheck
            }
        }
    }
`

/** The decisions taken for an ad campaign, newest first. */
export const AD_CAMPAIGN_DECISIONS = gql`
    query AdCampaignDecisions($adCampaignId: ID!, $kinds: [String!], $limit: Int) {
        AdCampaign {
            adCampaignDecisions(adCampaignId: $adCampaignId, kinds: $kinds, limit: $limit) {
                id
                kind
                actor
                status
                mode
                ad_set_external_id
                error
                decided_at
            }
        }
    }
`

const ACTION_RESULT_FIELDS = `
    ok
    reason
    message
    retryable
    warmup {
        ...WarmupFields
    }
`

export const CONFIRM_WARMUP_EXPANSION = gql`
    ${WARMUP_FIELDS}
    mutation ConfirmWarmupExpansion($adCampaignId: ID!, $expectedVersion: Int!) {
        confirmWarmupExpansion(adCampaignId: $adCampaignId, expectedVersion: $expectedVersion) {
            ${ACTION_RESULT_FIELDS}
        }
    }
`

export const DECLINE_WARMUP_EXPANSION = gql`
    ${WARMUP_FIELDS}
    mutation DeclineWarmupExpansion($adCampaignId: ID!, $expectedVersion: Int!) {
        declineWarmupExpansion(adCampaignId: $adCampaignId, expectedVersion: $expectedVersion) {
            ${ACTION_RESULT_FIELDS}
        }
    }
`

export const RETRY_WARMUP_EXPANSION = gql`
    ${WARMUP_FIELDS}
    mutation RetryWarmupExpansion($adCampaignId: ID!, $expectedVersion: Int!) {
        retryWarmupExpansion(adCampaignId: $adCampaignId, expectedVersion: $expectedVersion) {
            ${ACTION_RESULT_FIELDS}
        }
    }
`

export const SET_WARMUP_MODE = gql`
    ${WARMUP_FIELDS}
    mutation SetWarmupMode($adCampaignId: ID!, $expectedVersion: Int!, $mode: WarmupMode!) {
        setWarmupMode(adCampaignId: $adCampaignId, expectedVersion: $expectedVersion, mode: $mode) {
            ${ACTION_RESULT_FIELDS}
        }
    }
`

export const CANCEL_WARMUP = gql`
    mutation CancelWarmup($adCampaignId: ID!, $expectedVersion: Int!, $choice: String) {
        cancelWarmup(adCampaignId: $adCampaignId, expectedVersion: $expectedVersion, choice: $choice) {
            cancelled
            reason
            message
            retryable
            state
            version
            deleted
            started_directly
            converted_rows
            cleanup_pending
            choice_applied
            choice_reason
            resume_failed_code
        }
    }
`

export const REMOVE_WARMUP_EXPANSION_AD_SET = gql`
    mutation RemoveWarmupExpansionAdSet($adCampaignId: ID!, $adSetId: ID!) {
        removeWarmupExpansionAdSet(adCampaignId: $adCampaignId, adSetId: $adSetId) {
            removed
            reason
            message
        }
    }
`

export const DISMISS_WARMUP_NOTICE = gql`
    mutation DismissWarmupNotice($adCampaignId: ID!) {
        dismissWarmupNotice(adCampaignId: $adCampaignId)
    }
`

/** Reads Meta and settles an unconfirmed change of an ad set; never writes to Meta. */
export const RECHECK_AD_SET_CHANGE = gql`
    mutation RecheckAdSetChange($adSetId: ID!) {
        recheckAdSetChange(adSetId: $adSetId) {
            adSetId
            outcome
            message
        }
    }
`
